import { useParams, useSearchParams } from 'react-router-dom';
import { Container } from '@vnetwork/ui/layout';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@vnetwork/ui/primitives';
import { Alert } from '@vnetwork/ui/feedback';
import { useServiceDetail, type ServiceDetail } from '@/api/services';
import { TabError, TabLoading } from '@/pages/customers/detail/tab-states';
import { ServiceDetailHeader } from './service-detail-header';
import { ServiceInformationTab } from './service-information-tab';
import { ServiceConfigTab } from './service-config-tab';
import { ServicePricesTab } from './service-prices-tab';
import { ServiceStatementTab } from './service-statement-tab';
import { ServiceAlertsTab } from './service-alerts-tab';
import { ServiceMonitoringTab } from './service-monitoring-tab';
import { ServiceNotificationTab } from './service-notification-tab';
import { ServiceReceiptTab } from './service-receipt-tab';
import { ServiceSlaTab } from './service-sla-tab';
import { showConfigTab, showStatementTab, showAlertsTab, showMonitoringTab } from './service-tab-gating';

interface TabDef {
  value: string;
  label: string;
  show?: (s: ServiceDetail) => boolean;
  render: (s: ServiceDetail) => React.ReactNode;
}

/**
 * Full detail tab set (order mirrors legacy). Generic tabs always show; the
 * advanced tabs are template/status-gated via `service-tab-gating`.
 */
const TAB_DEFS: TabDef[] = [
  { value: 'information', label: 'Information', render: (s) => <ServiceInformationTab detail={s} /> },
  { value: 'configuration', label: 'Configuration', show: showConfigTab, render: (s) => <ServiceConfigTab detail={s} /> },
  { value: 'prices', label: 'Prices & VAT', render: (s) => <ServicePricesTab id={s.service_id} currency={s.service_currency} /> },
  { value: 'statement', label: 'Statement', show: showStatementTab, render: (s) => <ServiceStatementTab detail={s} /> },
  { value: 'alerts', label: 'Alerts', show: showAlertsTab, render: (s) => <ServiceAlertsTab id={s.service_id} /> },
  { value: 'monitoring', label: 'Monitoring', show: showMonitoringTab, render: (s) => <ServiceMonitoringTab detail={s} /> },
  { value: 'notification', label: 'Notifications', render: (s) => <ServiceNotificationTab id={s.service_id} /> },
  { value: 'receipt', label: 'Receipts', render: (s) => <ServiceReceiptTab id={s.service_id} currency={s.service_currency} /> },
  { value: 'sla', label: 'SLA', render: (s) => <ServiceSlaTab id={s.service_id} /> },
];

/**
 * Service detail page — identity header (breadcrumbs + name/status/template +
 * lifecycle actions) over a template/status-gated tab switcher persisted in
 * `?tab=` (lazy — each tab mounts only when active). Mirrors the contract detail
 * shell + the @ui detail recipe.
 */
export default function ServiceDetailPage() {
  const { id: idParam } = useParams();
  const id = Number(idParam) || 0;
  const [searchParams, setSearchParams] = useSearchParams();

  const { data, isLoading, isError, refetch } = useServiceDetail(id);
  const detail = data?.result;

  const visibleTabs = detail ? TAB_DEFS.filter((t) => !t.show || t.show(detail)) : [];
  const requestedTab = searchParams.get('tab');
  const activeTab = requestedTab && visibleTabs.some((t) => t.value === requestedTab) ? requestedTab : 'information';

  const onTabChange = (value: string) => {
    const next = new URLSearchParams(searchParams);
    next.set('tab', value);
    setSearchParams(next, { replace: true });
  };

  const nonVietnamese = detail?.customer_language && detail.customer_language !== 'vi';

  return (
    <Container size="full">
      {isError ? (
        <TabError title="Failed to load service" onRetry={() => refetch()} />
      ) : isLoading || !detail ? (
        <TabLoading rows={4} />
      ) : (
        <div style={{ display: 'grid', gap: 'var(--vn-space-4)' }}>
          {/* grid gap drives the vertical rhythm between the header, alert, and tab panel */}
          <ServiceDetailHeader detail={detail} />

          {nonVietnamese ? (
            <Alert tone="warning" title="Non-Vietnamese customer">
              This customer's language is{' '}
              <strong style={{ textTransform: 'uppercase' }}>{detail.customer_language}</strong>. Prepare
              documents accordingly.
            </Alert>
          ) : null}

          <Tabs value={activeTab} onValueChange={onTabChange}>
            <TabsList>
              {visibleTabs.map((tab) => (
                <TabsTrigger key={tab.value} value={tab.value}>
                  {tab.label}
                </TabsTrigger>
              ))}
            </TabsList>

            {visibleTabs.map((tab) => (
              <TabsContent key={tab.value} value={tab.value}>
                {activeTab === tab.value ? tab.render(detail) : null}
              </TabsContent>
            ))}
          </Tabs>
        </div>
      )}
    </Container>
  );
}
